import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import ErrorMessage from '@/components/common/ErrorMessage';
import FormHead from '@/components/common/Form/FormHead';
import FieldLayout from '@/components/Layout/FieldLayout';
import Button from '@/components/ui/Button';
import Label from '@/components/ui/Label';
import SearchSelectBox, {
  ISelectOption,
} from '@/components/ui/SearchSelectBox';
import { IDepartmentResponse } from '@/interfaces/department.model';
import {
  IAddEmployeeForm,
  IEmployeePageStates,
} from '@/interfaces/employee.model';
import { getFilterUrl, showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { IDesignationResponse } from '../../Designation/Designation';
import { IOfficeResponse } from '../../OfficeLocation/OfficeLocation';
import { IDeleteResponse } from '@/interfaces/common.model';
import { IUser } from '@/interfaces/user.model';
import { IShiftResponse } from '@/interfaces/Shits.model';
import { ICompany, ICompanyResponse } from '@/interfaces/company.model';
import { SingleValue } from 'react-select';
import { IOffice } from '@/interfaces/officelocation.model';
import { AppDispatch, RootState } from '@/redux/store';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { fetchUserDetails } from '@/redux/features/UserSlice';
interface IEmployeePermissionStep2Props {
  nextStep: () => void;
  imageFile: string | File | null;
  setPageStates: React.Dispatch<React.SetStateAction<IEmployeePageStates>>;
}
interface IAddEmployeeResponse extends IDeleteResponse {
  data: {
    userDetail: IUser;
    user: IAddEmployeeForm;
  };
}
const EmployeePermissionStep2 = ({
  imageFile,
  setPageStates,
  nextStep,
}: IEmployeePermissionStep2Props) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [companyList, setCompanyList] = useState<ICompany[] | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [officeList, setOfficeList] = useState<IOffice[] | null>(null);
  const {
    register,
    trigger,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<IAddEmployeeForm>();
  const asyncData: Record<string, ISelectOption[]> = {};
  const userDetails = useSelector((state: RootState) => state?.user?.user);
  let debounceTimer: NodeJS.Timeout | null = null;
  const departmentId = watch('departmentId');
  useEffect(() => {
    if (id) {
      const currentOffice = watch('officeId');
      const shift = watch('shiftId');
      const company = watch('companyId');
      setValue('shiftId', shift);
      setValue('companyId', company);
      setValue('officeId', currentOffice);
    }
  }, [id]);
  useEffect(() => {
    register('departmentId', { ...errorTypes.required('Department') });
    register('designationId', { ...errorTypes.required('Designation') });
    register('officeId', { ...errorTypes.required('office') });
    register('shiftId', { ...errorTypes.required('shift') });
    register('companyId', { ...errorTypes.required('Company') });
  }, [register]);

  const getDepartment = (inputValue: string): Promise<ISelectOption[]> => {
    return new Promise((resolve) => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        const key = inputValue.trim().toLowerCase();
        if (asyncData[key]) {
          resolve(asyncData[key]);
          return;
        }
        try {
          const url = `${API_URL}${API_ENDPOINT.getDepartment}?search=${key}`;
          const response: IDepartmentResponse = await callApi(url, {
            method: 'GET',
          });

          const data =
            response.status && response.data?.department?.length
              ? response.data.department.map((d) => ({
                  value: d.id,
                  label: d.name,
                }))
              : [];

          asyncData[key] = data;
          resolve(data);
        } catch (error) {
          console.error('Error fetching departments:', error);
          resolve([]);
        }
      }, 500);
    });
  };

  const getDesignation = async (
    inputValue?: string,
  ): Promise<ISelectOption[]> => {
    if (!departmentId) return [];

    const filter = {
      departmentId,
      searchQuery: inputValue,
    };

    try {
      const response: IDesignationResponse = await callApi(
        `${API_URL}${API_ENDPOINT.getDesigantion}?${getFilterUrl(filter)}`,
        { method: 'GET' },
      );

      return (
        response?.data?.designation?.map((d) => ({
          value: d.id,
          label: d.name,
        })) || []
      );
    } catch {
      return [];
    }
  };
  const getOffices = async (inputValue?: string): Promise<ISelectOption[]> => {
    const filter = {
      search: inputValue,
      pageNumber: 1,
      pageSize: 50,
      type: 'office',
    };

    const api = `${API_URL}${API_ENDPOINT.getSite}?${getFilterUrl(filter)}`;

    try {
      const response: IOfficeResponse = await callApi(api, {
        method: 'GET',
      });

      if (response.status && response.data?.site?.length) {
        // setValue('officeId', response.data.site[0].id);
        setOfficeList(response?.data?.site);
        return response.data.site.map((office) => ({
          value: office.id,
          label: office.name,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching offices:', error);
      return [];
    }
  };
  const handleCreateEmployee = async () => {
    const isValid = await trigger([
      'officeId',
      'departmentId',
      'designationId',
      'shiftId',
    ]);
    const formData = new FormData();
    if (!imageFile) {
      return showToastNotification(
        'danger',
        'No image selected. Please choose an image to continue.',
      );
    }

    if (isValid && imageFile) {
      setIsLoading(true);
      const data = getValues();
      formData.append('designationId', data?.designationId);
      formData.append('departmentId', data?.departmentId);
      formData.append('officeId', data?.officeId);
      formData.append('shiftId', data?.shiftId);
      formData.append('companyId', data?.companyId);
      if (!id) {
        formData.append(
          'name',
          (data?.firstName || '').trim() + ' ' + (data?.lastName || '').trim(),
        );
        formData.append('ecpName', data?.ecpName);
        formData.append('ecpPhone', data?.ecpPhone);
        formData.append('email', data?.email);
        formData.append('phone', data?.phone);
        formData.append('dob', data?.dob);
        formData.append('gender', String(data?.gender));
        // formData.append('shiftId', data?.shiftId);
        formData.append('bloodGroup', data?.bloodGroup);
        formData.append('profilePhoto', imageFile);
        formData.append('fatherName', data?.fatherName);
        formData.append('motherName', data?.motherName);
        formData.append('isMarried', data?.isMarried);
        formData.append('status', data?.status);
        formData.append('joiningDate', data?.joiningDate);
        formData.append('addingUser', String(true));
        formData.append('employeeCode', String(data?.employeeCode));
      }
      try {
        const response: IAddEmployeeResponse = await callApi(
          id
            ? API_URL + API_ENDPOINT.addEmployee + `?userId=${id}`
            : API_URL + API_ENDPOINT.addEmployee,
          {
            method: 'POST',
            data: formData,
            multipart: true,
          },
        );
        if (response?.status) {
          setPageStates((prev) => ({
            ...prev,
            empId: response?.data?.user?.id ?? null,
          }));
          showToastNotification('success', response?.message);
          if (id == userDetails?.id) {
            dispatch(fetchUserDetails());
          }
          nextStep();
        }
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error) {
          showToastNotification('danger', (error as IApiErrorResponse).message);
        } else {
          showToastNotification('danger', 'Something went wrong');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };
  const getShifts = async (inputValue?: string): Promise<ISelectOption[]> => {
    const filter = {
      search: inputValue,
    };

    let api = API_URL + API_ENDPOINT.getShift;

    if (inputValue) {
      api = `${API_URL}${API_ENDPOINT.getShift}?${getFilterUrl(filter)}`;
    }

    try {
      const response: IShiftResponse = await callApi(api, {
        method: 'GET',
      });
      // setValue('shiftId', response?.data?.shift[0]?.id);
      if (response.status && response.data?.shift?.length) {
        return response.data.shift.map((shift) => ({
          value: shift.id,
          label: shift.shiftName,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching shifts:', error);
      return [];
    }
  };
  const getCompany = async (inputValue?: string): Promise<ISelectOption[]> => {
    const filter = {
      search: inputValue,
    };

    let api = `${API_URL}${API_ENDPOINT.getCompany}`;

    if (inputValue) {
      api = `${API_URL}${API_ENDPOINT.getCompany}?${getFilterUrl(filter)}`;
    }

    try {
      const response: ICompanyResponse = await callApi(api, {
        method: 'GET',
      });
      // if (!watch('companyId')) {
      //   setValue('companyId', response?.data?.company[0]?.id);
      // }
      setCompanyList(response?.data?.company);
      if (response.status && response.data?.company?.length) {
        return response.data.company.map((company) => ({
          value: company.id,
          label: company.name,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching company:', error);
      return [];
    }
  };
  const handleOfficeChange = (selected: SingleValue<ISelectOption>) => {
    const selectedOffice = officeList?.filter(
      (office) => office?.id == selected?.value,
    );
    const selectedCompany = companyList?.filter(
      (company) => company?.id == selectedOffice?.[0]?.companyId,
    );
    setValue('companyId', selectedCompany?.[0]?.id ?? '');
  };
  return (
    <>
      <div className="row">
        <FormHead heading="Permission" />
        <FieldLayout fieldStyle="col-lg-6">
          <Label required={true} id="companyId">
            Select Company
          </Label>
          <Controller
            name="companyId"
            rules={errorTypes.required('Company')}
            render={({ field }) => (
              <SearchSelectBox
                value={field.value}
                title="Select Company"
                onChange={(option) => field.onChange(option?.value || '')}
                isAsync
                fetchOptions={getCompany}
                defaultFetch
                disable={isLoading}
                isUsingFilter
              />
            )}
          />
          <ErrorMessage message={errors?.companyId?.message} />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-6">
          <Label required={true} id="officeId">
            Office
          </Label>
          <Controller
            name="officeId"
            rules={errorTypes.required('officeId')}
            render={({ field }) => (
              <SearchSelectBox
                value={field.value}
                title="Select Office"
                onChange={(option) => {
                  field.onChange(option?.value || '');
                  handleOfficeChange(option);
                }}
                isAsync
                fetchOptions={getOffices}
                defaultFetch
                disable={isLoading}
                isUsingFilter
              />
            )}
          />
          <ErrorMessage message={errors?.officeId?.message} />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-6">
          <Label required={true} id="shiftId">
            Select Shift
          </Label>
          <Controller
            name="shiftId"
            rules={errorTypes.required('shift')}
            render={({ field }) => (
              <SearchSelectBox
                value={field.value}
                title="Select Shift"
                onChange={(option) => field.onChange(option?.value || '')}
                isAsync
                fetchOptions={getShifts}
                defaultFetch
                disable={isLoading}
                isUsingFilter
              />
            )}
          />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-6">
          <Label required={true} id="departmentId">
            Deparment
          </Label>
          <Controller
            name="departmentId"
            rules={errorTypes.required('Department')}
            render={({ field }) => (
              <SearchSelectBox
                value={field.value}
                title="Select department"
                onChange={(option) => {
                  field.onChange(option?.value || '');
                  setValue('designationId', '');
                }}
                isAsync
                fetchOptions={getDepartment}
                defaultFetch
                disable={isLoading}
                isUsingFilter
              />
            )}
          />
          <ErrorMessage message={errors?.departmentId?.message} />
        </FieldLayout>
        <FieldLayout fieldStyle="col-lg-6">
          <Label required={true} id="designationId">
            Designation
          </Label>
          <Controller
            name="designationId"
            rules={errorTypes.required('Designation')}
            render={({ field }) => (
              <SearchSelectBox
                value={field.value}
                key={departmentId}
                title="Select designation"
                onChange={(option) => field.onChange(option?.value || '')}
                isAsync
                fetchOptions={getDesignation}
                defaultFetch
                disable={isLoading}
                isUsingFilter
              />
            )}
          />
          <ErrorMessage message={errors?.departmentId?.message} />
        </FieldLayout>
      </div>
      <div className="d-flex align-items-center justify-content-end gap-4">
        <Button
          className="btn btn-primary px-4"
          type="button"
          onClick={handleCreateEmployee}
          isLoading={isLoading}
        >
          {id ? 'Update and Continue' : 'Create Employee'}
        </Button>
      </div>
    </>
  );
};
export default EmployeePermissionStep2;
